/**
 * reportPdfExport.js — Branded A4 PDF export for portal reports.
 * Generates a jsPDF document with Spacemancraft letterhead, portal accent color,
 * all section types, page numbers, and an optional "PENDING APPROVAL" watermark.
 */
import jsPDF from 'jspdf';
import { getApp } from 'firebase/app';
import { getFirestore, addDoc, collection, serverTimestamp } from 'firebase/firestore';

// ── Layout constants (mm) ─────────────────────────────────────────────────────
const PW  = 210;
const PH  = 297;
const ML  = 18;   // left margin
const MR  = 18;   // right margin
const HDR = 25;   // header height
const FTR = 12;   // footer height
const CT  = HDR + 3;   // content top y
const CB  = PH - FTR - 3;  // content bottom y
const TW  = PW - ML - MR;  // text width

// ── Helpers ───────────────────────────────────────────────────────────────────
function hexRgb(hex) {
  const h = (hex || '#0e9cc0').replace('#', '');
  if (h.length === 3) return [
    parseInt(h[0]+h[0], 16),
    parseInt(h[1]+h[1], 16),
    parseInt(h[2]+h[2], 16),
  ];
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes >= 1024*1024) return `${(bytes/1024/1024).toFixed(1)} MB`;
  return `${Math.round(bytes/1024)} KB`;
}

function extOf(name) { return (name || '').split('.').pop().toLowerCase(); }

function isImgExt(name) {
  return ['jpg','jpeg','png','gif','webp'].includes(extOf(name));
}

async function fetchBase64(url) {
  try {
    const resp = await fetch(url, { mode: 'cors' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return new Promise((res) => {
      const r = new FileReader();
      r.onload  = () => res(r.result);
      r.onerror = () => res(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ── Per-page renderers ────────────────────────────────────────────────────────
function drawHeader(doc, label, accentRgb) {
  doc.setFillColor(10, 22, 40);
  doc.rect(0, 0, PW, HDR, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('SPACEMANCRAFT', ML, 11);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(accentRgb[0], accentRgb[1], accentRgb[2]);
  doc.text((label || 'REPORT').toUpperCase(), ML, 18);

  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    new Date().toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }),
    PW - MR, 11, { align: 'right' },
  );

  doc.setDrawColor(14, 156, 192);
  doc.setLineWidth(0.6);
  doc.line(0, HDR, PW, HDR);
}

function drawFooter(doc, pageNum, total) {
  const fy = PH - FTR;
  doc.setFillColor(10, 22, 40);
  doc.rect(0, fy, PW, FTR, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139);
  doc.text('Spacemancraft Private Limited · CIN: U34300TN2022PTC153940', ML, fy + 7);
  doc.text(`Page ${pageNum} of ${total}`, PW - MR, fy + 7, { align: 'right' });
}

function drawWatermark(doc) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(44);
  doc.setTextColor(210, 60, 60);
  doc.text('PENDING APPROVAL', PW / 2, PH / 2, { align: 'center', angle: 45 });
}

// ── Main export function ──────────────────────────────────────────────────────
export async function exportReportPDF(post, portalConfig, userId) {
  const label     = portalConfig?.label || 'Portal Report';
  const color     = portalConfig?.color || '#0e9cc0';
  const accent    = hexRgb(color);
  const isPending = post.adminApproved === false;

  const doc = new jsPDF('p', 'mm', 'a4');
  let y = CT;

  function startPage() {
    drawHeader(doc, label, accent);
    if (isPending) drawWatermark(doc);
    y = CT;
  }

  function newPage() {
    doc.addPage();
    startPage();
  }

  function ensureY(needed) {
    if (y + needed > CB) newPage();
  }

  // ── Page 1 ─────────────────────────────────────────────────────────────────
  startPage();

  // Portal / type line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text(`${label.toUpperCase()} · REPORT`, ML, y);
  y += 5;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(30, 41, 59);
  const titleLines = doc.splitTextToSize(post.title || 'Untitled', TW);
  doc.text(titleLines, ML, y);
  y += titleLines.length * 7;

  // Author / date
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Author: ${post.uploadedBy || '—'}    ·    Date: ${fmtDate(post.uploadedAt)}`,
    ML, y,
  );
  y += 5;

  // Category / tags
  if (post.category || (post.tags||[]).length) {
    doc.setFontSize(7.5);
    doc.setTextColor(accent[0], accent[1], accent[2]);
    const meta = [post.category, ...(post.tags||[])].filter(Boolean).join('  ·  ');
    doc.text(meta.toUpperCase(), ML, y);
    y += 4;
  }

  // Accent rule
  doc.setDrawColor(accent[0], accent[1], accent[2]);
  doc.setLineWidth(0.5);
  doc.line(ML, y, PW - MR, y);
  y += 6;

  // ── Sections ───────────────────────────────────────────────────────────────
  let sectionNum = 0;
  let figNum     = 0;

  for (const sec of (post.sections || [])) {
    switch (sec.type) {

      case 'heading': {
        const lvl = sec.level || 1;
        if (lvl === 1) sectionNum++;
        const sz   = lvl === 1 ? 13 : lvl === 2 ? 11 : 10;
        const gap  = lvl === 1 ? 5 : lvl === 2 ? 4 : 3;
        const pref = lvl === 1 ? `${sectionNum}. ` : '';
        const lines = doc.splitTextToSize(pref + (sec.text || ''), TW);
        ensureY(sz * 0.45 * lines.length + gap + 3);
        if (lvl === 1) { y += 2; }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(sz);
        doc.setTextColor(
          lvl <= 2 ? accent[0] : 50,
          lvl <= 2 ? accent[1] : 50,
          lvl <= 2 ? accent[2] : 50,
        );
        doc.text(lines, ML, y);
        y += lines.length * sz * 0.45 + gap;
        break;
      }

      case 'paragraph': {
        const text = (sec.text || '').trim();
        if (!text) break;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        const lines = doc.splitTextToSize(text, TW);
        const LH = 5;
        for (let li = 0; li < lines.length; ) {
          const fits = Math.floor((CB - y) / LH);
          if (fits <= 0) { newPage(); continue; }
          const chunk = lines.slice(li, li + fits);
          doc.text(chunk, ML, y);
          y += chunk.length * LH;
          li += chunk.length;
        }
        y += 3;
        break;
      }

      case 'bullets': {
        const items = (sec.items || []).filter(Boolean);
        if (!items.length) break;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        for (const item of items) {
          const lines = doc.splitTextToSize(`• ${item}`, TW - 4);
          ensureY(lines.length * 5 + 2);
          doc.text(lines, ML + 3, y);
          y += lines.length * 5 + 1;
        }
        y += 2;
        break;
      }

      case 'code': {
        const codeText = sec.text || '';
        if (!codeText.trim()) break;
        const lang  = (sec.language || '').toUpperCase();
        const lines = doc.splitTextToSize(codeText, TW - 8);
        const LH    = 4.5;
        const blockH = lines.length * LH + 8;

        ensureY(Math.min(blockH, 40));

        if (lang) {
          doc.setFillColor(232, 232, 240);
          doc.rect(ML, y, TW, 6, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(120, 120, 140);
          doc.text(lang, ML + 3, y + 4);
          y += 6;
        }

        // Code background
        const totalCodeH = lines.length * LH + 8;
        doc.setFillColor(245, 245, 248);
        doc.rect(ML, y, TW, Math.min(totalCodeH, CB - y), 'F');
        doc.setDrawColor(200, 200, 215);
        doc.setLineWidth(0.3);
        doc.rect(ML, y, TW, Math.min(totalCodeH, CB - y));
        doc.setFont('courier', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(40, 40, 55);
        y += 5;

        for (let li = 0; li < lines.length; ) {
          const fits = Math.floor((CB - y) / LH);
          if (fits <= 0) {
            newPage();
            doc.setFillColor(245, 245, 248);
            doc.rect(ML, y, TW, Math.min((lines.length - li) * LH + 6, CB - y), 'F');
            doc.setDrawColor(200, 200, 215);
            doc.rect(ML, y, TW, Math.min((lines.length - li) * LH + 6, CB - y));
            y += 5;
            continue;
          }
          const chunk = lines.slice(li, li + fits);
          doc.text(chunk, ML + 3, y);
          y += chunk.length * LH;
          li += chunk.length;
        }
        y += 6;
        break;
      }

      case 'image':
      case 'graph': {
        figNum++;
        const capH   = sec.caption ? 5 : 0;
        const descH  = sec.description ? 8 : 0;
        const imgH   = 70;
        ensureY(imgH + capH + descH + 6);

        if (sec.url && isImgExt(sec.name || sec.url)) {
          const b64 = await fetchBase64(sec.url);
          if (b64) {
            const fmt = extOf(sec.name || sec.url) === 'png' ? 'PNG' : 'JPEG';
            try {
              doc.addImage(b64, fmt, ML, y, TW, imgH, undefined, 'FAST');
            } catch {
              imgPlaceholder(doc, y, imgH, '[Image failed to load]');
            }
          } else {
            imgPlaceholder(doc, y, imgH, '[Image unavailable]');
          }
        } else {
          const label2 = extOf(sec.name || '').toUpperCase() || 'FILE';
          imgPlaceholder(doc, y, 28, `[ ${label2} — open attachment to view ]`);
          y += 28 - imgH; // adjust offset below
        }
        y += imgH + 2;

        if (sec.caption) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          doc.setTextColor(30, 41, 59);
          doc.text(`Fig ${figNum} – ${sec.caption}`, ML, y);
          y += 5;
        }
        if (sec.description) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8.5);
          doc.setTextColor(100, 116, 139);
          const dl = doc.splitTextToSize(sec.description, TW);
          doc.text(dl, ML, y);
          y += dl.length * 4.5 + 3;
        }
        y += 3;
        break;
      }

      case 'attachment':
      case 'cad': {
        ensureY(10);
        const icon = sec.type === 'cad' ? '⚖' : '■';
        const name = sec.name || 'attachment';
        const sz   = sec.size ? ` (${fmtSize(sec.size)})` : '';
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(`${icon}  ${name}${sz}`, ML + 3, y);
        if (sec.url) {
          doc.setTextColor(accent[0], accent[1], accent[2]);
          doc.textWithLink('→ Open', PW - MR - 14, y, { url: sec.url });
        }
        y += 7;
        break;
      }

      default: break;
    }
  }

  // ── Footers on all pages ───────────────────────────────────────────────────
  const total = doc.internal.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawFooter(doc, p, total);
  }

  // ── Access log ─────────────────────────────────────────────────────────────
  try {
    const db = getFirestore(getApp());
    await addDoc(collection(db, 'access_logs'), {
      uid:        userId || null,
      action:     'report_pdf_export',
      postId:     post.id   || null,
      postTitle:  post.title || null,
      portal:     label,
      exportedAt: serverTimestamp(),
    });
  } catch (_) {}

  // ── Save ───────────────────────────────────────────────────────────────────
  const filename = `${(post.title||'report').replace(/[^a-z0-9]/gi,'_').slice(0,60)}.pdf`;
  doc.save(filename);
}

function imgPlaceholder(doc, y, h, msg) {
  doc.setFillColor(240, 240, 248);
  doc.rect(ML, y, TW, h, 'F');
  doc.setDrawColor(200, 200, 215);
  doc.setLineWidth(0.3);
  doc.rect(ML, y, TW, h);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(160, 160, 175);
  doc.text(msg, ML + TW / 2, y + h / 2, { align: 'center' });
}
